/**
 * Unified WorldState demo entry point
 * Mounts demos based on which elements are present in the DOM
 */
import { DisposePool } from "@phosphor/utils";
import { getDefaultStore } from "jotai";
import { createRoot } from "react-dom/client";
import { WorldStateImpl } from "#scripts/vmc/WorldState-impl.ts";
import { DemoApp } from "#scripts/world-state-demo/DemoApp.tsx";
import { rootLogger } from "#scripts/world-state-demo/rootLogger.ts";
import { createScrollyRectangle } from "#scripts/world-state-demo/scrolly-rectangle-demo/createScrollyRectangle.ts";
import { type RemoteCursor, setupPresence } from "#scripts/world-state-demo/setupPresence.ts";
import { setupScrollObservers } from "#scripts/world-state-demo/setupScrollObservers.tsx";

// Bootstrap demos when DOM is ready
function bootstrap() {
  const scrollyDemoElement = document.getElementById("scrolly-world-demo");

  if (!scrollyDemoElement) {
    // No demo elements found, silently exit
    console.warn("WorldState demo: no demo elements found");
    return;
  }

  rootLogger._debug("Initializing scrolly rectangle demo...");

  // Create Jotai store and dispose pool
  const store = getDefaultStore();
  const pool = new DisposePool();

  // Create WorldState instance
  const world = new WorldStateImpl(rootLogger, {
    store,
    pool,
    plugins: [],
    strict: true,
  });

  rootLogger._debug("Mounting scrolly rectangle demo");

  // Create the scrolly rectangle entity
  const rectangleUID = createScrollyRectangle(world);

  // Set up scroll observers
  setupScrollObservers(world, scrollyDemoElement, pool);

  // Enable dev mode by default for the demo
  import("#scripts/lib/dev/IfDev.tsx").then(({ isDevAtom }) => {
    store.set(isDevAtom, true);
  });

  // Mount with DevTools wrapper
  const root = createRoot(scrollyDemoElement);

  let cursorSetter: ((cursors: RemoteCursor[]) => void) | null = null;

  root.render(
    <DemoApp
      world={world}
      rectangleUID={rectangleUID}
      onCursorsMount={(setter) => {
        cursorSetter = setter;
      }}
    />,
  );

  // Set up presence connection after render
  setupPresence(rootLogger, pool, (cursors) => {
    cursorSetter?.(cursors);
  });

  rootLogger._debug("Scrolly rectangle demo mounted successfully");
}

// Auto-initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootstrap);
} else {
  bootstrap();
}
